'use client';
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, Flame, Handshake } from 'lucide-react';

export default function CTABanner() {
  const router = useRouter();

  useEffect(() => {
    router.prefetch('/user/signup');
  }, [router]);

  const handleSignUp = useCallback(
    (e) => {
      e.preventDefault();
      router.push('/user/signup');
    },
    [router]
  );

  return (
    <section
      className="relative overflow-hidden px-6 2xl:py-24 xl:py-20 lg:py-16 md:py-12 py-10"
      style={{
        background: `
          radial-gradient(ellipse 80% 100% at 50% 120%, rgba(220,38,38,0.18) 0%, transparent 60%),
          radial-gradient(ellipse 60% 80% at 10% 50%, rgba(234,88,12,0.15) 0%, transparent 60%),
          radial-gradient(ellipse 60% 80% at 90% 50%, rgba(245,158,11,0.12) 0%, transparent 60%),
          var(--bg-secondary)
        `,
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-4"
        style={{
          backgroundImage:
            'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative mx-auto max-w-[900px] text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <div className="mb-7 flex justify-center gap-2">
            {[16, 24, 16].map((size, i) => (
              <Flame key={i} size={size} color={i === 1 ? '#F59E0B' : '#EA580C'} className={i === 1 ? 'opacity-100' : 'opacity-60'} />
            ))}
          </div>

          <h2 className="font-display mb-5 text-[clamp(36px,6vw,72px)] leading-[1.05]">
            Start Investing <span className="text-ember">Today</span>
          </h2>

          <p className="mx-auto mb-10 max-w-[560px] text-[clamp(16px,2vw,20px)] leading-relaxed text-text-muted">
            Join over 1M+ investors already growing their wealth with MineMe. Your financial future starts now.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/user/signup"
              onClick={handleSignUp}
              className="inline-flex items-center gap-2 rounded-xl bg-linear-to-br from-accent-amber to-accent-orange px-10 py-4 font-display text-[17px] font-bold text-white shadow-[0_8px_40px_rgba(245,158,11,0.3)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_50px_rgba(245,158,11,0.45)]"
            >
              Sign Up Free <ArrowRight size={18} />
            </a>
            <a
              href="#plans"
              className="inline-flex items-center gap-2 rounded-xl border border-border px-9 py-4 font-display text-[17px] font-semibold text-text-primary transition hover:border-accent-amber hover:text-accent-amber"
            >
              Explore Plans
            </a>
            <Link
              href="/investment-sectors"
              className="inline-flex items-center gap-2 rounded-xl border border-border px-9 py-4 font-display text-[17px] font-semibold text-text-primary transition hover:border-accent-amber hover:text-accent-amber"
            >
              <Handshake size={18} aria-hidden /> Investment sectors
            </Link>
          </div>

          <p className="mt-7 text-xs text-text-muted sm:text-sm">
            No lock-in to get started · 24/7 Support · Payouts verified on-chain
          </p>
        </motion.div>
      </div>
    </section>
  );
}
